import React, { useState, useEffect } from 'react';
import { useHistory } from "react-router-dom";
import './Sidebar.css'; // Import CSS for styling
import menuConfig, { MenuItemConfig, SubMenuItemConfig } from "./SidebarConfig";
import { SwitchNetwork } from './NetworkSwitcher';
import { isInvalidPath } from "./invalidPaths";

interface SidebarProps {
  chainId: number;
}

const Sidebar: React.FC<SidebarProps> = (props) => {
  const { chainId } = props;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  const [activeSubSubMenu, setActiveSubSubMenu] = useState<string | null>(null);
  const history = useHistory();

  const toggleExpandOpen = () => {
    setIsExpanded(true);
  };
  const toggleExpandClose = () => {
    setIsExpanded(false);
  };

  useEffect(() => {
    // Function to determine initial expand state based on screen size
    const handleResize = () => {
      const isSmallScreen = window.innerWidth < 768; // Define "small screen" as less than 768px
      setIsExpanded(isSmallScreen);
    };
    handleResize();
  }, []);

  const handleItemClick = async (
    currentChain: number,
    targetChainId: number,
    path: string,
    isSubSubMenu: boolean
  ) => {
    if (!isInvalidPath(targetChainId, path)) {
      if (currentChain !== targetChainId && targetChainId !== 0) {
        // Change networks if required
        try {
          await SwitchNetwork(`0x${targetChainId.toString(16)}`);
          // After switching networks, navigate to the path
          window.location.href = path;
        } catch (error) {
          console.error('Error switching network:', error);
        }
      } else if (targetChainId === 0 || path.startsWith('http')) {
        // Handle external links by opening them in the same window
        window.location.href = path;
      } else if (isSubSubMenu) {
        // Internal sub-submenu link
        history.push(path);
      } else {
        // Other internal links
        window.location.href = path;
      }
    }
  };

  const handleMenuItemClick = async (
    expanded: boolean,
    currentChain: number,
    targetChainId: number,
    item: MenuItemConfig
  ) => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    if (mediaQuery.matches && (item.subMenu || !expanded)) {
      // on small screen open submenus if available
      toggleExpandOpen();
      setActiveSubMenu(activeSubMenu === item.title ? null : item.title);
    } else {
      handleItemClick(currentChain, targetChainId, item.path, false);
    }
  };

  const handleArrowClick = (item: MenuItemConfig) => {
    if (item.subMenu) {
      setActiveSubMenu(activeSubMenu === item.title ? null : item.title);
    }
  };

  const handleSubArrowClick = (subItem: SubMenuItemConfig) => {
    if (subItem.subSubMenu) {
      setActiveSubSubMenu(activeSubSubMenu === subItem.name ? null : subItem.name);
    }
  };

  return (
    <div
      className={`icon-bar ${isExpanded ? 'expanded' : ''}`}
      onMouseEnter={toggleExpandOpen}
      onMouseLeave={toggleExpandClose}
    >
      <div className="submenu-container">
        {menuConfig.map((item) => (
          <div key={item.title} className="menu-item-container">
            <div className="icon-container">
              <div className="sidebar-menu-item">
                <item.icon
                  className="icon"
                  onClick={() => handleMenuItemClick(isExpanded, chainId, chainId, item)}
                />
                <span
                  className="menu-title"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleMenuItemClick(isExpanded, chainId, chainId, item)}
                  onKeyPress={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      handleMenuItemClick(isExpanded, chainId, chainId, item);
                    }
                  }}
                >
                  {item.title}
                </span>
                {isExpanded && item.subMenu && (
                  <span
                    className="arrow"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleArrowClick(item)}
                    onKeyPress={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        handleArrowClick(item);
                      }
                    }}
                  >
                    {activeSubMenu === item.title ? '▼' : '▶'}
                  </span>
                )}
              </div>
            </div>
            {isExpanded && activeSubMenu === item.title && item.subMenu && (
              <div className="sub-menu">
                {item.subMenu.map((subItem) => (
                  <div key={subItem.name} className="sub-menu-item">
                    <div className="submenu-title-container">
                      {/* Display the logo image next to the network name */}
                      {subItem.logo && (
                        <img
                          src={subItem.logo}
                          alt={`${subItem.name} Logo`}
                          className="submenu-logo"
                        />
                      )}
                      <span
                        className="submenu-title"
                        role="button"
                        tabIndex={0}
                        onClick={() => handleItemClick(chainId, subItem.networkId, subItem.path, false)}
                        onKeyPress={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            handleItemClick(chainId, subItem.networkId, subItem.path, false);
                          }
                        }}
                      >
                        {subItem.name}
                      </span>
                      {subItem.subSubMenu && (
                        <span
                          className={`submenu-arrow ${
                            activeSubSubMenu === subItem.name ? 'submenu-arrow-active' : ''
                          }`}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSubArrowClick(subItem)}
                          onKeyPress={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              handleSubArrowClick(subItem);
                            }
                          }}
                        >
                          {activeSubSubMenu === subItem.name ? '▼' : '▶'}
                        </span>
                      )}
                    </div>
                    {activeSubSubMenu === subItem.name && subItem.subSubMenu && (
                      <div className="sub-sub-menu">
                        {subItem.subSubMenu.map((subSubItem) => (
                          <span
                            key={subSubItem.name}
                            className="sub-submenu-title"
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              handleItemClick(chainId, subItem.networkId, subSubItem.path, true)
                            }
                            onKeyPress={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                handleItemClick(chainId, subItem.networkId, subSubItem.path, true);
                              }
                            }}
                          >
                            {subSubItem.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Sidebar;
